<template>
  <div>
    <div class="separator" />
    <DashboardCard :border="false" :firstContent="true" class="mt-0" :padding="false">
      <template v-slot:description>
        <div>
          <div>
            <h3 class="table-title">{{$t('widgets.wholesalerTable.title')}}</h3>
            <p>{{$t('widgets.wholesalerTable.description')}}</p>
          </div>
        </div>
      </template>
      <template v-slot:search>
        <div class="d-flex justify-content-end align-items-end h-100">
          <div class="input-with-icons mb-4">
            <MDBIcon fab icon="search" class="icon-left" @click="triggerSearch"/>
            <input
                class="mb-0"
                v-model="searchInput"
                type="text"
                :placeholder="$t('widgets.wholesalerTable.searchPlaceholder')"
                @keydown.enter.prevent="triggerSearch"
            />
            <button
                v-if="searchInput"
                type="button"
                class="icon-right"
                @click="searchInput=''"
            >
              <MDBIcon fas icon="times" />
            </button>
          </div>
        </div>
      </template>
      <template v-slot:table>
        <div class="table-wrapper">
          <MDBTable borderless class="table-dashboard score-table width-unset" v-show="(rows && firstLoad) || !loading">
            <thead>
            <tr>
              <th
                  v-for="col in visibleColumns"
                  :key="col.key"
                  class="col"
                  :aria-sort="ariaSortFor(col.key)"
                  @click="isSortable(col.key) && toggleSort(col.key)"
                  :class="['col', `col-${col.key}`, alignClass(col), columnClass(col)]"
                  role="columnheader"
                  :tabindex="isSortable(col.key) ? 0 : -1"
                  @keydown.enter.prevent="isSortable(col.key) && toggleSort(col.key)"
                  @keydown.space.prevent="isSortable(col.key) && toggleSort(col.key)"
              >
                  <span class="th-content">
                    {{ col.label }}
                    <span v-if="isSortable(col.key)" class="sort-indicator" aria-hidden="true">
                      <span class="chev up" :class="{ active: sort_by === col.key && sort_order === 'asc' }">▲</span>
                      <span class="chev down" :class="{ active: sort_by === col.key && sort_order === 'desc' }">▼</span>
                    </span>
                  </span>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="!rows.length">
              <td :colspan="visibleColumns.length" class="text-center py-4">No data</td>
            </tr>
            <tr v-else v-for="(row, idx) in rows" :key="idx">
              <td
                  v-for="col in visibleColumns"
                  :key="col.key"
                  :class="['col', `col-${col.key}`, alignClass(col), columnClass(col), isNegative(row, col) ? 'negative' : '']"
              >
                <template v-if="col.key === 'details'">
                  <button
                      type="button"
                      class="btn p-0 text-decoration-underline"
                      @click="openDetailsModal(getDetailsUserId(row))"
                  >
                    details
                  </button>
                </template>
                <template v-else>
                  {{ formatCell(row, col) }}
                </template>
              </td>
            </tr>
            </tbody>
            <Loader :show="loading" v-if="loading" />
          </MDBTable>
        </div>

        <div class="pagination-wrapper d-flex justify-content-between align-items-center mt-4">
          <div class="pagination-left">
            <DownloadAttribute
              v-if="showDownload"
              :button-label="t('widgets.wholesalerTable.downloadButton')"
              button-class="rounded-8 text-capitalize"
            />
          </div>

          <ul class="pagination mb-0">
            <li class="page-item" :class="{ disabled: meta.page === 1 }">
              <button class="page-link" @click="goToPage(meta.page - 1)" :disabled="meta.page === 1"><MDBIcon fas icon="angle-left" /></button>
            </li>

            <li v-for="p in pagesToShow" :key="p" class="page-item" :class="{ active: meta.page === p }">
              <button class="page-link" @click="goToPage(p)">{{ p }}</button>
            </li>

            <li class="page-item" :class="{ disabled: meta.page === meta.pages }">
              <button class="page-link" @click="goToPage(meta.page + 1)" :disabled="meta.page === meta.pages"><MDBIcon fas icon="angle-right" /></button>
            </li>
          </ul>

          <div class="dropdown-wrapper">
            <select v-model="selectedPerPage" @change="changePerPage" class="dropdown">
              <option v-for="opt in perPageOptions" :key="opt" :value="opt">{{ opt }}</option>
            </select>
            <span class="dropdown-info">
              {{ pageStart }} to {{ pageEnd }} of {{ meta.total }}
            </span>
          </div>

        </div>

      </template>
    </DashboardCard>

    <MDBModal v-model="modal" tabindex="-1" centered size="xl">
      <PointsOverviewModalNew v-if="detailsId"
                           :user_id="detailsId"/>
    </MDBModal>
  </div>
</template>

<script setup>
import Loader from "@/components/General/Loader/index.vue";
import DashboardCard from "../User/Wholesaler/DashboardCard.vue";
import { MDBTable, MDBModal, MDBIcon } from "mdb-vue-ui-kit";
import { ref, reactive, computed, onMounted, defineProps } from "vue";
import PointsOverviewModalNew from "../User/Seller/PointsOverviewModalNew.vue";
import { useI18n } from 'vue-i18n'
import DownloadAttribute from "../DownloadAttribute.vue";
import { KpiDashboardService } from "@/services/kpi-dashboard.service";

const { t } = useI18n();

const modal = ref(false);
const detailsId = ref(null);

const props = defineProps({
  data: { type: Object, default: () => ({})}
})

const allColumns = [
  { label: t('widgets.wholesalerTable.columns.customer_number'), key: 'customer_number', width: 9, align: 'left', numeric: true, sortable: true },
  { label: t('widgets.wholesalerTable.columns.address_company_name'), key: 'address_company_name', width: 30, align: 'left', sortable: true },
  { label: t('widgets.wholesalerTable.columns.address_street'), key: 'address_street', width: 20, align: 'left', sortable: true },
  { label: t('widgets.wholesalerTable.columns.address_postal_code'), key: 'address_postal_code', width: 15, align: 'left', sortable: true },
  { label: t('widgets.wholesalerTable.columns.address_city'), key: 'address_city', width: 20, align: 'left', sortable: true },
  { label: t('widgets.wholesalerTable.columns.last_registration'), key: 'last_registration', width: 17, align: 'left', sortable: true },
  { label: t('widgets.wholesalerTable.columns.network'), key: 'network', width: 15, align: 'left', sortable: true },
  { label: t('widgets.wholesalerTable.columns.profile_group'), key: 'profile_group', width: 15, align: 'left', sortable: true },
  { label: t('widgets.wholesalerTable.columns.tires_amount'), key: 'tires_amount', width: 13, align: 'left', numeric: true, highlightNegative: true, sortable: true },
  { label: t('widgets.wholesalerTable.columns.point_balance'), key: 'point_balance', width: 13, align: 'left', numeric: true, highlightNegative: true, sortable: true },
  { label: t('widgets.wholesalerTable.columns.bonus'), key: 'bonus', width: 10, align: 'left', numeric: true, highlightNegative: true, sortable: true },
  { label: t('widgets.wholesalerTable.columns.details'), key: 'details', width: 10, align: 'center', sortable: false, action: true },
]

const rows = ref([]);
const loading = ref(false);
const firstLoad = ref(false);

const meta = reactive({
  total: 0,
  per_page: 20,
  page: 1,
  pages: 0,
  sort_by: null,
  sort_order: null,
  search: null,
});

const metadata = ref({
  show_all: true,
  show_download: true,
});

const sort_by = ref("last_registration");
const sort_order = ref("desc");
const searchInput = ref("");
const search = ref("");
const selectedPerPage = ref(meta.per_page);

const wholesalerListData = computed(() => {
  return Array.isArray(props.data?.data) ? props.data.data : [];
});

const visibleColumns = computed(() => {
  if (!rows.value.length) {
    return allColumns;
  }
  const availableKeys = new Set();
  rows.value.forEach((row) => {
    if (row && typeof row === "object") {
      Object.keys(row).forEach((key) => availableKeys.add(key));
    }
  });
  return allColumns.filter((col) => col.action || availableKeys.has(col.key));
});

const totalWidth = computed(() => visibleColumns.value.reduce((sum, col) => sum + (col.width || 0), 0));

function initializeFromProps() {
  rows.value = [...wholesalerListData.value];

  if (props.data && Object.keys(props.data).length > 0) {
    const { ...metaInfo } = props.data;

    Object.assign(meta, {
      total: metaInfo.total || 0,
      per_page: metaInfo.per_page || 20,
      page: metaInfo.page || 1,
      pages: metaInfo.pages || 0,
      sort_by: metaInfo.sort_by || null,
      sort_order: metaInfo.sort_order || null,
      search: metaInfo.search || null
    });

    if (metaInfo.metadata) {
      metadata.value = {
        show_all: metaInfo.metadata.show_all !== false,
        show_download: metaInfo.metadata.show_download !== false,
      };
    }

    if (meta.search) {
      search.value = meta.search;
      searchInput.value = meta.search;
    }

    if (!metaInfo.pages && meta.total && meta.per_page) {
      meta.pages = Math.ceil(meta.total / meta.per_page);
    }

    if (meta.sort_by) sort_by.value = meta.sort_by;
    if (meta.sort_order) sort_order.value = meta.sort_order;
  }

  selectedPerPage.value = meta.per_page;
}

const perPageOptions = computed(() => {
  const baseOptions = [10, 20, 50, 100];
  if (metadata.value.show_all) {
    return [...baseOptions, "Show All"];
  }
  return baseOptions;
});

const showDownload = computed(() => metadata.value.show_download);

const pageStart = computed(() => {
  if (!rows.value.length) return 0;
  return (meta.page - 1) * meta.per_page + 1;
});

const pageEnd = computed(() => {
  if (!rows.value.length) return 0;
  if (selectedPerPage.value === "Show All") {
    return meta.total;
  }
  return Math.min(meta.page * meta.per_page, meta.total);
});

const pagesToShow = computed(() => {
  const pages = [];
  const totalPages = meta.pages || 1;
  const current = meta.page;
  let start = Math.max(1, current - 2);
  let end = Math.min(totalPages, current + 2);
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
});

function goToPage(page) {
  if (page < 1 || page > meta.pages || loading.value) return;
  meta.page = page;
  fetchRows();
}

async function loadAllRows() {
  if (loading.value) return;

  try {
    loading.value = true;
    const baseParams = {
      per_page: 1000,
      ...(sort_by.value && { sort_by: sort_by.value }),
      ...(sort_order.value && { sort_order: sort_order.value }),
      ...(search.value && { search: search.value }),
    };

    const firstResp = await KpiDashboardService.fetchCustomers({ ...baseParams, page: 1 });
    const firstList = firstResp?.wholesaler_list ?? firstResp ?? {};
    const allData = Array.isArray(firstList.data) ? [...firstList.data] : [];
    const total = Number(firstList.total ?? allData.length);
    const totalPages = Number(
      firstList.pages ?? (Math.ceil(total / 1000) || 1),
    );

    for (let page = 2; page <= totalPages; page++) {
      const nextResp = await KpiDashboardService.fetchCustomers({ ...baseParams, page });
      const nextList = nextResp?.wholesaler_list ?? nextResp ?? {};
      if (Array.isArray(nextList.data)) {
        allData.push(...nextList.data);
      }
    }

    rows.value = allData;

    Object.assign(meta, {
      total,
      per_page: total,
      page: 1,
      pages: 1,
    });

  } catch (e) {
    console.error("Error loading all rows:", e);
  } finally {
    loading.value = false;
  }
}

function changePerPage() {
  if(selectedPerPage.value === "Show All"){
    loadAllRows();
  } else {
    meta.per_page = Number(selectedPerPage.value);
    meta.page = 1;
    fetchRows();
  }
}

function isSortable(key) {
  const col = visibleColumns.value.find(c => c.key === key);
  return !!col && col.sortable !== false;
}

function toggleSort(key) {
  if(loading.value) return
  if (sort_by.value === key) {
    sort_order.value = sort_order.value === "asc" ? "desc" : "asc";
  } else {
    sort_by.value = key;
    sort_order.value = "asc";
  }
  fetchRows(true);
}

function ariaSortFor(key) {
  if (sort_by.value !== key) return "none";
  return sort_order.value === "asc" ? "ascending" : "descending";
}

function formatCell(row, col) {
  const v = row?.[col.key];
  if (v === null || v === undefined || v === "" || v === 'Unknown') return "";
  return v;
}

function isNegative(row, col) {
  if (!col.highlightNegative) return false;
  const v = row?.[col.key];
  return typeof v === "number" && v < 0;
}

function alignClass(col) {
  switch (col.align) {
    case "right": return "align-right";
    case "center": return "align-center";
    default: return "align-left";
  }
}

const columnClass = (col) => {
  if (col.width && totalWidth.value > 0) {
    const percent = Math.round((col.width / totalWidth.value) * 100);
    return `w-${percent}`;
  }
  return '';
};

const openDetailsModal = async (id) => {
  if (id == null || id === "") {
    return;
  }
  modal.value = true;
  detailsId.value = String(id);
};

function getDetailsUserId(row) {
  // Prefer dealer/user id — never fall back to customer_number (API expects users.id).
  const id = row?.user_data?.id ?? row?.dealer_id ?? null;
  if (id === "" || id == null) {
    return null;
  }
  return id;
}

async function fetchRows() {
  loading.value = true;
  try {
    const params = {
      page: meta.page,
      per_page: meta.per_page,
      ...(sort_by.value && { sort_by: sort_by.value }),
      ...(sort_order.value && { sort_order: sort_order.value }),
      ...(search.value && { search: search.value })
    };

    const resp = await KpiDashboardService.fetchCustomers(params);

    const cl = resp?.wholesaler_list ?? resp ?? {};

    rows.value = Array.isArray(cl.data) ? cl.data : [];

    meta.total = Number(cl.total ?? rows.value.length);
    meta.per_page = Number(cl.per_page ?? meta.per_page);
    meta.page = Number(cl.page ?? meta.page);

    if (cl.pages) {
      meta.pages = Number(cl.pages);
    } else {
      meta.pages = Math.ceil(meta.total / meta.per_page);
    }

    if (cl.sort_by) sort_by.value = cl.sort_by;
    if (cl.sort_order) sort_order.value = cl.sort_order;

  } catch (err) {
    console.error("Error fetching wholesaler rows:", err);
    rows.value = [];
    meta.total = 0;
    meta.pages = 0;
  } finally {
    loading.value = false;
  }
}

function triggerSearch() {
  rows.value = [];
  search.value = (searchInput.value || "").trim() || null;
  fetchRows(true);
}

onMounted(() => {
  initializeFromProps();
  firstLoad.value = true;

  if (!rows.value.length) {
    fetchRows();
  }
});
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/table_dashboard.scss";

.sort-indicator .chev {
  opacity: 0.3;
  font-size: 10px;
  line-height: 1;
}

.sort-indicator .chev.active {
  opacity: 1;
}

.col {
  padding: 8px 12px;
}

.th-content:hover {
  cursor: pointer;
}

.separator {
  width: 100%;
  margin: 40px 0 70px 0;
  height: 1px;
  background-color: #DBDBDB;
}

.table-title{
  font-weight: 500;
}

.pagination-wrapper {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem;
}

.pagination-left {
  justify-self: start;
}

.pagination {
  justify-self: center;
}

.dropdown-wrapper {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
